import { useIsMounted } from "@pancakeswap/hooks";
import { AtomBox } from "@pancakeswap/ui/components/AtomBox";
import throttle from "lodash/throttle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import BottomNav from "../../components/BottomNav";
import { Box } from "../../components/Box";
import Flex from "../../components/Box/Flex";
import CakePrice from "../../components/CakePrice/CakePrice";
import CbonPrice from "../../components/CakePrice/CbonPrice";
import PriceChip from "../../components/CakePrice/PriceChip";
import Footer from "../../components/Footer";
import LangSelector from "../../components/LangSelector/LangSelector";
import MenuItems from "../../components/MenuItems/MenuItems";
import { SubMenuItems } from "../../components/SubMenuItems";
import { useMatchBreakpoints } from "../../contexts";
import Logo from "./components/Logo";
import { MENU_HEIGHT, MOBILE_MENU_HEIGHT, TOP_BANNER_HEIGHT, TOP_BANNER_HEIGHT_MOBILE } from "./config";
import { MenuContext } from "./context";
import { NavProps } from "./types";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
`;

const StyledNav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${MENU_HEIGHT}px;
  background-color: ${({ theme }) => theme.nav.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: ${({ theme }) => theme.shadows.row};
  transform: translate3d(0, 0, 0);

  padding-left: 16px;
  padding-right: 16px;
  /* Header fit (Bez, 2026-10-03: at ~1550 px with a wallet side panel the bar ran into itself). Nothing in the bar wraps; the
     right side folds by width instead: full price pills from 1320 px, one price chip from 968 px, no chip and no language
     globe between 852 and 967 px (the globe is in the footer too), the wallet address from 1100 px (avatar only from 852
     px). No network dropdown (BNB Chain only); a wrong-network button appears only while the wallet is on another chain.
     The phone bar (< 852 px) is unchanged.
     Token search (Bez, 2026-10-04; the field is the web app's rightSide): a 240-280 px field from 1260 px and a search button
     below that. To make room the full price pills start at 1600 px (the chip covers 968-1599) and the globe is left out
     between 852 and 1259 px.
     Competitions tab (Bez, 2026-10-06; six tabs now): tabs are 10 px apart each side (was 16), the wordmark logo starts at
     1080 px (the mark covers 852-1079) and the globe is left out up to 1439 px. Measured free space at 852 / 968 / 1080 /
     1260 / 1600 px stays positive (evidence stage-nav-competitions-20261006).
     Prices back (Bez, 2026-10-07: from 968 to 1599 px only the two coin icons showed): the chip shows both prices stacked
     ($0.00000026 at 13 px) from 1024 px, and on the phone bar from 576 px; 968-1023 keeps the icons only. Room for it: the
     wordmark starts at 1200 px (the mark covers 852-1199) and the search field at 1366 px (a search button below). Measured
     free space and screenshots: evidence stage-prices-chart-20261007. */
  white-space: nowrap;
  > * {
    flex: none;
  }
  /* A wallet on another chain (the amber "Switch to BNB Chain" button, web WrongNetworkPill): the button takes the prices' room
     until it is resolved. Measured 2026-10-07 with the e2e header-fit wrong-network pass: production 1115dd6 already ran past the
     edge at 1260-1280 and 1600-1728 px in this state (up to 144 px), and the price chip would have added 1500-1599. */
  &:has([data-testid="wrong-network"]) [data-header-prices] {
    display: none;
  }
`;
const PricesFull = styled.div`
  display: none;
  @media screen and (min-width: 1600px) {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-right: 16px;
  }
`;
const PricesCompact = styled.div`
  display: none;
  @media screen and (min-width: 576px) and (max-width: 851.98px), screen and (min-width: 968px) and (max-width: 1599.98px) {
    display: block;
    margin-right: 8px;
  }
`;
const LangBox = styled(Box)`
  @media screen and (min-width: 852px) and (max-width: 1439.98px) {
    display: none;
  }
`;

const FixedContainer = styled.div<{ showMenu: boolean; height: number }>`
  position: fixed;
  top: ${({ showMenu, height }) => (showMenu ? 0 : `-${height}px`)};
  left: 0;
  transition: top 0.2s;
  height: ${({ height }) => `${height}px`};
  width: 100%;
  z-index: 20;
`;

const TopBannerContainer = styled.div<{ height: number }>`
  height: ${({ height }) => `${height}px`};
  min-height: ${({ height }) => `${height}px`};
  max-height: ${({ height }) => `${height}px`};
  width: 100%;
`;

const BodyWrapper = styled(Box)`
  position: relative;
  display: flex;
  max-width: 100vw;
`;

const Inner = styled.div`
  flex-grow: 1;
  transition: margin-top 0.2s, margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translate3d(0, 0, 0);
  max-width: 100%;
`;

const Menu: React.FC<React.PropsWithChildren<NavProps>> = ({
  linkComponent = "a",
  banner,
  rightSide,
  isDark,
  toggleTheme,
  currentLang,
  setLang,
  cakePriceUsd,
  cbonPriceUsd,
  links,
  subLinks,
  footerLinks,
  activeItem,
  activeSubItem,
  bottomNav,
  langs,
  buyCakeLabel,
  buyCakeLink,
  children,
  chainId,
}) => {
  const { isMobile } = useMatchBreakpoints();
  const isMounted = useIsMounted();
  const [showMenu, setShowMenu] = useState(true);
  const refPrevOffset = useRef(typeof window === "undefined" ? 0 : window.pageYOffset);

  const topBannerHeight = isMobile ? TOP_BANNER_HEIGHT_MOBILE : TOP_BANNER_HEIGHT;

  const totalTopMenuHeight = isMounted && banner ? MENU_HEIGHT + topBannerHeight : MENU_HEIGHT;

  useEffect(() => {
    const handleScroll = () => {
      const currentOffset = window.pageYOffset;
      const isBottomOfPage = window.document.body.clientHeight === currentOffset + window.innerHeight;
      const isTopOfPage = currentOffset === 0;
      // Always show the menu when user reach the top
      if (isTopOfPage) {
        setShowMenu(true);
      }
      // Avoid triggering anything at the bottom because of layout shift
      else if (!isBottomOfPage) {
        if (currentOffset < refPrevOffset.current || currentOffset <= totalTopMenuHeight) {
          // Has scroll up
          setShowMenu(true);
        } else {
          // Has scroll down
          setShowMenu(false);
        }
      }
      refPrevOffset.current = currentOffset;
    };
    const throttledHandleScroll = throttle(handleScroll, 200);

    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [totalTopMenuHeight]);

  // Find the home link if provided
  const homeLink = links.find((link) => link.label === "Home");

  const subLinksWithoutMobile = subLinks?.filter((subLink) => !subLink.isMobileOnly);
  const subLinksMobileOnly = subLinks?.filter((subLink) => subLink.isMobileOnly);
  const providerValue = useMemo(() => ({ linkComponent }), [linkComponent]);
  return (
    <MenuContext.Provider value={providerValue}>
      <AtomBox
        asChild
        minHeight={{
          xs: "auto",
          md: "100vh",
        }}
      >
        <Wrapper>
          <FixedContainer style={{ border: "none" }} showMenu={showMenu} height={totalTopMenuHeight}>
            {banner && isMounted && <TopBannerContainer height={topBannerHeight}>{banner}</TopBannerContainer>}
            <StyledNav>
              <Flex>
                <Logo href={homeLink?.href ?? "/"} />
                <AtomBox display={{ xs: "none", md: "block" }}>
                  <MenuItems items={links} activeItem={activeItem} activeSubItem={activeSubItem} ml="24px" />
                </AtomBox>
              </Flex>
              <Flex alignItems="center" height="100%">
                <PricesFull data-testid="prices-full" data-header-prices>
                  <CakePrice chainId={chainId} showSkeleton={false} cakePriceUsd={cakePriceUsd} />
                  <CbonPrice chainId={chainId} showSkeleton={false} cakePriceUsd={cbonPriceUsd} />
                </PricesFull>
                <PricesCompact data-header-prices>
                  <PriceChip chainId={chainId} cadinuPriceUsd={cakePriceUsd} cbonPriceUsd={cbonPriceUsd} />
                </PricesCompact>
                <LangBox mt="4px">
                  <LangSelector
                    currentLang={currentLang}
                    langs={langs}
                    setLang={setLang}
                    buttonScale="xs"
                    color="textSubtle"
                    hideLanguage
                  />
                </LangBox>
                {rightSide}
              </Flex>
            </StyledNav>
          </FixedContainer>
          {subLinks ? (
            <Flex justifyContent="space-around" overflow="hidden">
              <SubMenuItems
                items={subLinksWithoutMobile}
                mt={`${totalTopMenuHeight + 1}px`}
                activeItem={activeSubItem}
              />

              {subLinksMobileOnly && subLinksMobileOnly?.length > 0 && (
                <SubMenuItems
                  items={subLinksMobileOnly}
                  mt={`${totalTopMenuHeight + 1}px`}
                  activeItem={activeSubItem}
                  isMobileOnly
                />
              )}
            </Flex>
          ) : (
            <div />
          )}
          <BodyWrapper mt={!subLinks ? `${totalTopMenuHeight + 1}px` : "0"}>
            <Inner>{children}</Inner>
          </BodyWrapper>
        </Wrapper>
      </AtomBox>
      <Footer
        chainId={chainId}
        items={footerLinks}
        isDark={isDark}
        toggleTheme={toggleTheme}
        langs={langs}
        setLang={setLang}
        currentLang={currentLang}
        cakePriceUsd={cakePriceUsd}
        buyCakeLabel={buyCakeLabel}
        buyCakeLink={buyCakeLink}
        // room for the whole bottom bar (44 px items + its padding and border = 51 px, plus the phone's safe area) and a little air,
        // for every width the bar shows at (below 852 px). The old [44px, null, 0] ended at 576 px (this theme's array starts at
        // 370 px), so from 576 to 851 px the bar covered the footer's language button.
        mb={[`calc(${MOBILE_MENU_HEIGHT + 20}px + env(safe-area-inset-bottom))`, null, null, "0px"]}
      />
      <AtomBox display={{ xs: "block", md: "none" }}>
        <BottomNav
          items={bottomNav?.links ?? links}
          activeItem={bottomNav ? bottomNav.activeItem : activeItem}
          activeSubItem={bottomNav ? bottomNav.activeSubItem : activeSubItem}
        />
      </AtomBox>
    </MenuContext.Provider>
  );
};

export default Menu;
